import { useEffect, useState } from "react";
import { api, formatError, fmtDuration } from "@/lib/api";
import { X, Check, CaretRight, CaretLeft, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STEPS = ["General", "Provider", "Playback", "Metadata", "Artwork", "Review"];
const HLS_RE = /^https?:\/\/.+\.m3u8(\?.*)?$/i;

export function AddAssetWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [providers, setProviders] = useState([]);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    name: "", asset_type: "Movie", description: "", provider: "ReVine",
    hls_url: "", provider_asset_id: "", source_url: "",
    duration_seconds: 0, resolution: "1080p", aspect_ratio: "16:9",
    audio_language: "en", captions_available: false, thumbnail: "", poster: "",
  });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const hlsValid = HLS_RE.test(form.hls_url.trim());

  useEffect(() => {
    api.get("/assets/providers").then((r) => setProviders(r.data));
    api.get("/assets/types").then((r) => setTypes(r.data));
  }, []);

  const next = () => {
    if (step === 0 && !form.name.trim()) { toast.error("Asset name is required"); return; }
    if (step === 2 && !hlsValid) { toast.error("Enter a valid HLS .m3u8 URL"); return; }
    setStep(Math.min(step + 1, STEPS.length - 1));
  };
  const back = () => setStep(Math.max(step - 1, 0));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, duration_seconds: parseInt(form.duration_seconds || 0, 10) };
      const { data } = await api.post("/assets", payload);
      toast.success("Asset registered");
      onCreated(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#141414] border border-[#262626] rounded-sm w-full max-w-2xl max-h-[90vh] flex flex-col ccs-enter">
        <div className="px-5 py-3 border-b border-[#262626] flex items-center justify-between">
          <h3 className="font-head font-semibold text-sm">Add Existing Asset</h3>
          <button data-testid="asset-wizard-close" onClick={onClose} className="text-[#737373] hover:text-white transition-colors duration-150"><X size={18} /></button>
        </div>

        <div className="flex items-center gap-1 px-5 py-3 border-b border-[#262626] overflow-x-auto">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1 shrink-0">
              <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-mono ${
                i < step ? "bg-emerald-500 text-black" : i === step ? "bg-[#3A86FF] text-white" : "bg-[#0A0A0A] border border-[#262626] text-[#525252]"}`}>
                {i < step ? <Check size={11} weight="bold" /> : i + 1}
              </div>
              <span className={`text-xs ${i === step ? "text-white" : "text-[#525252]"}`}>{s}</span>
              {i < STEPS.length - 1 && <CaretRight size={12} className="text-[#333] mx-1" />}
            </div>
          ))}
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {step === 0 && (
            <div className="space-y-4">
              <Field label="Asset name" testid="asset-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Asset type</label>
                <select data-testid="asset-type" value={form.asset_type} onChange={(e) => set("asset_type", e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none">
                  {(types.length ? types : ["Movie"]).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Description (optional)</label>
                <textarea data-testid="asset-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2}
                  className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none transition-colors duration-150" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-2 font-bold">Asset Provider</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(providers.length ? providers : ["ReVine"]).map((p) => (
                  <button key={p} data-testid={`provider-${p}`} onClick={() => set("provider", p)}
                    className={`p-3 rounded-sm border text-sm text-left transition-colors duration-150 ${
                      form.provider === p ? "border-[#3A86FF] bg-[#3A86FF]/10 text-white" : "border-[#262626] text-[#A3A3A3] hover:border-[#3a3a3a]"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">HLS Manifest URL (.m3u8)</label>
                <div className="relative">
                  <input data-testid="asset-hls-url" value={form.hls_url} onChange={(e) => set("hls_url", e.target.value)}
                    placeholder="https://cdn.example.com/stream/index.m3u8"
                    className={`w-full bg-[#0A0A0A] border rounded-sm px-3 py-2.5 pr-9 text-sm outline-none transition-colors duration-150 ${
                      form.hls_url ? (hlsValid ? "border-emerald-500/50" : "border-red-500/50") : "border-[#262626] focus:border-[#3A86FF]"}`} />
                  {form.hls_url && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                      {hlsValid ? <CheckCircle size={16} className="text-emerald-500" /> : <WarningCircle size={16} className="text-red-500" />}
                    </span>
                  )}
                </div>
                {form.hls_url && !hlsValid && <p data-testid="hls-error" className="text-[11px] text-red-500 mt-1">Must be an http(s) URL ending in .m3u8</p>}
              </div>
              <Field label="Provider Asset ID (optional)" testid="asset-source-id" value={form.provider_asset_id} onChange={(e) => set("provider_asset_id", e.target.value)} />
              <Field label="Source URL (optional)" testid="asset-source-url" value={form.source_url} onChange={(e) => set("source_url", e.target.value)} />
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Duration (seconds)</label>
                <input data-testid="asset-duration" type="number" min="0" value={form.duration_seconds} onChange={(e) => set("duration_seconds", e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none" />
                <p className="text-[10px] text-[#525252] mt-1 font-mono">{fmtDuration(parseInt(form.duration_seconds || 0, 10))}</p>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Resolution</label>
                <select data-testid="asset-resolution" value={form.resolution} onChange={(e) => set("resolution", e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none">
                  <option value="2160p">2160p — 4K</option><option value="1080p">1080p</option><option value="720p">720p</option><option value="480p">480p</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Aspect Ratio</label>
                <select data-testid="asset-aspect" value={form.aspect_ratio} onChange={(e) => set("aspect_ratio", e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none">
                  <option value="16:9">16:9</option><option value="4:3">4:3</option><option value="9:16">9:16</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Audio Language (optional)</label>
                <input data-testid="asset-audio-lang" value={form.audio_language} onChange={(e) => set("audio_language", e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none" />
              </div>
              <label className="col-span-2 flex items-center gap-2 text-sm text-[#A3A3A3] cursor-pointer">
                <input data-testid="asset-captions" type="checkbox" checked={form.captions_available} onChange={(e) => set("captions_available", e.target.checked)} className="accent-[#3A86FF]" />
                Closed captions available
              </label>
            </div>
          )}

          {step === 4 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <Field label="Thumbnail URL (optional)" testid="asset-thumbnail" value={form.thumbnail} onChange={(e) => set("thumbnail", e.target.value)} />
                <Field label="Poster URL (optional)" testid="asset-poster" value={form.poster} onChange={(e) => set("poster", e.target.value)} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Preview</label>
                <div className="aspect-video bg-[#0A0A0A] border border-[#262626] rounded-sm overflow-hidden flex items-center justify-center">
                  {form.thumbnail
                    ? <img src={form.thumbnail} alt="" className="w-full h-full object-cover" />
                    : <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#333]">No artwork</span>}
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <h4 className="font-head font-semibold text-sm mb-2">Asset Summary</h4>
                <Row label="Name" value={form.name} />
                <Row label="Type" value={form.asset_type} />
                <Row label="Provider" value={form.provider} />
                <Row label="Provider Asset ID" value={form.provider_asset_id || "—"} />
                <Row label="Playback URL" value={<span className="font-mono text-[10px] text-emerald-400 break-all">{form.hls_url}</span>} />
                <Row label="Duration" value={fmtDuration(parseInt(form.duration_seconds || 0, 10)) || "—"} />
                <Row label="Resolution" value={`${form.resolution} · ${form.aspect_ratio}`} />
                <p className="text-[10px] text-[#525252] pt-1">A permanent Internal Asset Reference (AST-######) is assigned automatically on save.</p>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Artwork</label>
                <div className="aspect-video bg-[#0A0A0A] border border-[#262626] rounded-sm overflow-hidden flex items-center justify-center">
                  {form.thumbnail
                    ? <img src={form.thumbnail} alt="" className="w-full h-full object-cover" />
                    : <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#333]">No artwork</span>}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#262626] flex justify-between">
          <button data-testid="asset-wizard-back" onClick={back} disabled={step === 0}
            className="flex items-center gap-1 px-4 py-2 text-sm text-[#A3A3A3] hover:text-white disabled:opacity-30 transition-colors duration-150">
            <CaretLeft size={14} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button data-testid="asset-wizard-next" onClick={next}
              className="flex items-center gap-1 px-4 py-2 text-sm bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 rounded-sm font-medium">
              Next <CaretRight size={14} />
            </button>
          ) : (
            <button data-testid="asset-wizard-save" onClick={save} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 transition-colors duration-150 rounded-sm font-medium disabled:opacity-60">
              <Check size={16} weight="bold" /> {saving ? "Saving…" : "Save Asset"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">{label}</label>
      <input data-testid={testid} {...props}
        className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2.5 text-sm outline-none transition-colors duration-150" />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 border-b border-[#1c1c1c] py-2">
      <span className="text-xs text-[#737373] shrink-0">{label}</span>
      <span className="text-sm text-right min-w-0">{value}</span>
    </div>
  );
}
